import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC } from "react";
import * as Yup from "yup";
import useAuth from "src/hooks/useAuth";
import http from "src/utils/httpHelper";
import { useSnackbar } from "notistack";
import UserInterface from "src/models/user";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import i18n from "src/i18n/i18n";

interface EditProfileFormDialogProps {
    isOpen: boolean,
    toggleEditFormDialog: any
}

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .max(255)
        .required(i18n.t("VALIDATION.REQUIRED")),
    email: Yup.string()
        .email(i18n.t("VALIDATION.EMAIL"))
        .max(255)
        .required(i18n.t("VALIDATION.REQUIRED")),
});

const EditProfileFormDialog: FC<EditProfileFormDialogProps> = ({ isOpen, toggleEditFormDialog }) => {
    const { t } = useTranslation();
    const { setUser, user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: user.email,
            name: user.name,
        }
    });

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={isOpen}
                onClose={() => { toggleEditFormDialog(); reset(); }}
            >
                <DialogTitle
                    sx={{
                        p: 3,
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        {t("MY_ACCOUNT.ACCOUNT_EDIT")}
                    </Typography>
                    <Typography variant="subtitle2">
                        {t("MY_ACCOUNT.ACCOUNT_EDIT")}
                    </Typography>
                </DialogTitle>
                <form onSubmit={handleSubmit(async (values) => {
                    return await http.put('/user/profile-information', values)
                        .then(async (response) => {
                            return await http.get<UserInterface>("/api/user")
                                .then((response) => {
                                    enqueueSnackbar(t('MY_ACCOUNT.PROFILE_INFO_UPDATED'), { variant: 'success' });
                                    setUser(response.data);
                                    toggleEditFormDialog();
                                })
                                .catch((error) => {
                                    enqueueSnackbar(t('MY_ACCOUNT.PROFILE_INFO_UPDATED_WITH_ERROR'), { variant: 'warning' });
                                });
                        })
                        .catch((error) => {
                            console.error(error);
                            enqueueSnackbar(error.message, { variant: 'error' });
                        })
                })}>
                    <DialogContent
                        dividers
                        sx={{
                            p: 3,
                        }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={12}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    error={Boolean(errors.name)}
                                                    fullWidth
                                                    helperText={errors.name?.message}
                                                    label={t("FIELDS.NAME")}
                                                    name="name"
                                                    variant="outlined"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="email"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    error={Boolean(errors.email)}
                                                    fullWidth
                                                    helperText={errors.email?.message}
                                                    label={t("Email address")}
                                                    name="email"
                                                    type="email"
                                                    variant="outlined"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            p: 3,
                        }}
                    >
                        <Button color="secondary" onClick={toggleEditFormDialog}>
                            {t("GENERAL.CANCEL")}
                        </Button>
                        <Button
                            type="submit"
                            startIcon={
                                isSubmitting ? <CircularProgress size="1rem" /> : null
                            }
                            disabled={isSubmitting || !isValid}
                            variant="contained"
                        >
                            {t("MY_ACCOUNT.ACCOUNT_EDIT")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}

export default EditProfileFormDialog;